/**
 * Slack OAuth Flow Handler
 *
 * Handles the OAuth popup flow for connecting Slack workspaces to KMS processes.
 * Opens a popup window, monitors for completion, and handles success/error states.
 */

const OAUTH_POPUP_WIDTH = 600;
const OAUTH_POPUP_HEIGHT = 700;

export interface OAuthResult {
  success: boolean;
  error?: string;
}

/**
 * Calculate popup window position (centered on screen)
 */
function getPopupPosition(): { left: number; top: number } {
  const dualScreenLeft = window.screenLeft !== undefined ? window.screenLeft : window.screenX;
  const dualScreenTop = window.screenTop !== undefined ? window.screenTop : window.screenY;

  const width = window.innerWidth || document.documentElement.clientWidth || screen.width;
  const height = window.innerHeight || document.documentElement.clientHeight || screen.height;

  const left = width / 2 - OAUTH_POPUP_WIDTH / 2 + dualScreenLeft;
  const top = height / 2 - OAUTH_POPUP_HEIGHT / 2 + dualScreenTop;

  return { left, top };
}

/**
 * Open Slack OAuth popup and monitor for completion
 *
 * The popup will redirect to the backend OAuth callback URL, which will
 * then redirect back to the frontend with success/error parameters.
 *
 * @param oauthUrl - The OAuth URL to open (from backend)
 * @param onComplete - Callback when OAuth completes (success or error)
 * @returns Popup window reference
 */
export function openSlackOAuthPopup(
  oauthUrl: string,
  onComplete: (result: OAuthResult) => void
): Window | null {
  const { left, top } = getPopupPosition();

  // Window features
  const features = [
    `width=${OAUTH_POPUP_WIDTH}`,
    `height=${OAUTH_POPUP_HEIGHT}`,
    `left=${left}`,
    `top=${top}`,
    'menubar=no',
    'toolbar=no',
    'location=no',
    'status=no',
    'resizable=yes',
    'scrollbars=yes',
  ].join(',');

  // Open popup
  const popup = window.open(oauthUrl, 'slack-oauth', features);

  if (!popup) {
    onComplete({
      success: false,
      error: 'Popup blocked. Please allow popups for this site.',
    });
    return null;
  }

  // Focus popup
  popup.focus();

  // Poll for popup close or URL change (OAuth callback)
  const pollInterval = setInterval(() => {
    try {
      // Check if popup was closed by user
      if (popup.closed) {
        clearInterval(pollInterval);
        // User closed popup - treat as cancellation
        // Don't call onComplete here, as the user might reopen it
        return;
      }

      // Try to read popup URL (will throw if cross-origin)
      // Once it redirects back to our domain after OAuth, we can read it
      const popupUrl = popup.location.href;

      // Check if redirected back to our domain with success/error params
      if (popupUrl.includes(window.location.origin)) {
        const url = new URL(popupUrl);
        const status = url.searchParams.get('status');
        const error = url.searchParams.get('error');

        if (status === 'success') {
          clearInterval(pollInterval);
          popup.close();
          onComplete({ success: true });
        } else if (error) {
          clearInterval(pollInterval);
          popup.close();
          onComplete({ success: false, error: decodeURIComponent(error) });
        }
      }
    } catch (e) {
      // Cross-origin error - popup is still on Slack domain, continue polling
      // This is expected and normal
    }
  }, 500); // Poll every 500ms

  // Cleanup after 10 minutes (failsafe)
  setTimeout(
    () => {
      clearInterval(pollInterval);
      if (!popup.closed) {
        popup.close();
        onComplete({
          success: false,
          error: 'OAuth flow timed out. Please try again.',
        });
      }
    },
    10 * 60 * 1000
  );

  return popup;
}

/**
 * Alternative approach: Use message passing (more reliable but requires backend support)
 *
 * If the backend OAuth callback page posts a message to the opener window,
 * we can listen for it instead of polling the URL.
 */
export function listenForOAuthMessage(onComplete: (result: OAuthResult) => void): () => void {
  const handler = (event: MessageEvent) => {
    // Verify message origin (security check)
    if (event.origin !== window.location.origin) {
      return;
    }

    // Check if it's an OAuth result message
    if (event.data && event.data.type === 'slack-oauth-result') {
      onComplete({
        success: event.data.success,
        error: event.data.error,
      });
    }
  };

  window.addEventListener('message', handler);

  // Return cleanup function
  return () => {
    window.removeEventListener('message', handler);
  };
}
